<template>
    <loading v-if="fetchingLimits"/>
    <div v-else class="bg-white shadow mb-64 rounded max-w-1440 mx-auto relative">

        <!-- Only if limit has been reached we'll blur and show different text -->
        <overlay-locked v-if="limitReached"
                        :cancelButtonText="$t('applications.back_to_overview')"
                        :confirmButtonText="$t('subscriptions.go_to_upgrade')"
                        :description="$t('subscriptions.limit_reached', {slots: limits.slots})"
                        :showCancelButton="true"
                        :showConfirmButton="true"
                        cancelButtonLink="jobs"
                        confirmButtonLink="settings.pricing-plans">
        </overlay-locked>

        <div :style="limitReached ? `filter: blur(1.5rem);` : ''" class="p-32 space-y-16">
            <translatable #default="{ languages, selectedLanguage }" :errors="warningTabsTitle">
                <div v-for="lang in languages" :key="lang">
                    <div v-show="lang === selectedLanguage" class="grid grid-cols-2 gap-32">
                        <div class="flex-1">
                            <simple-input
                                v-model="form.title[lang]"
                                autofocus
                                @input="updateSlug($event, lang)"
                            >
                                {{ $t('jobs.title') }}
                                <span v-if="languages.length > 1">({{ lang }})</span>
                            </simple-input>
                        </div>

                        <div class="flex-1">
                            <simple-input
                                no-spacing-before
                                :prefix="currentCompany.jobsite_url + '/' + lang + '/'"
                                :value="form.slug[lang]"
                                @input="updateSlugFromInput($event, lang)"
                            >

                                {{ $t('jobs.slug') }}
                                <span v-if="languages.length > 1">({{ lang }})</span>

                                <template #after>
                                    <button class="w-full h-full flex items-center justify-center" @click="toggleSlugUpdating(lang)">
                                        <svg-icon :type="slugShouldUpdate[lang] ? 'link' : 'link-off'" />
                                    </button>
                                </template>

                            </simple-input>
                        </div>
                    </div>
                </div>
            </translatable>

            <div class="grid grid-cols-2 gap-32">
                <select-dropdown
                    v-model="form.request_reason_ids"
                    reference="id"
                    label="title"
                    :translatable="true"
                    :auto-translate="false"
                    :multiple="true"
                    :loading="fetchingReasons"
                    :options="requestReasons"
                    :placeholder="$t('form.dropdown.placeholder')"
                    class="flex-1"
                    :input-label="$t('settings.vacancy.reasons.request')"
                />
            </div>

            <h2 class="text-body font-semibold first-letter:uppercase">
                {{ $t('jobs.create.headers.general_info') }}
            </h2>

            <div class="grid grid-cols-2 gap-32">
                <div>
                    <job-location-editor
                        v-model="form"
                        :fetch-filters="false"
                        class="space-y-16"
                    />
                </div>

                <div class="space-y-16">
                    <select-dropdown
                        v-model="form.contract_type"
                        :auto-translate="false"
                        :disabled="availableContractTypes.length === 1"
                        :options="availableContractTypes"
                        :input-label="$t('jobs.labels.contract_type')"
                    />
                    <select-dropdown
                        v-model="form.status"
                        :auto-translate="false"
                        :disabled="availableStatuses.length === 1"
                        :options="availableStatuses"
                        :input-label="$t('jobs.labels.status')"
                    />
                    <select-dropdown
                        v-model="form.wanted_experience"
                        :auto-translate="false"
                        :options="availableWantedExperiences"
                        @change="wantedExperienceChanged($event)"
                        :input-label="$t('jobs.labels.wanted_experience')"
                    />
                    <simple-input
                        v-model="form.reference_number"
                        :input-label="referenceNumberLabelPrefix + $t('jobs.reference_number')"
                    />
                </div>
            </div>

            <div>
                <div class="text-body-sm font-semibold first-letter:uppercase text-black mb-4">
                    {{ $t('jobs.labels.timing') }}
                </div>
                <div v-if="fetchingAvailableParameters" class="inline-block">
                    <loading size="small"></loading>
                </div>
                <div v-else class="grid grid-cols-3 gap-8">
                    <div
                        v-for="x in availableTimings"
                        :key="'x-' + x.value"
                        class="p-8 border border-primary rounded cursor-pointer"
                    >
                        <checkbox
                            v-model="form.timing"
                            :value="x.value"
                        >
                            {{ x.label }}
                        </checkbox>
                    </div>
                </div>
            </div>

            <div>
                <div class="text-body-sm font-semibold first-letter:uppercase text-black mb-4">
                    {{ $t('job_parameters.type.label') }}
                </div>
                <div>
                    <div v-if="fetchingAvailableParameters" class="inline-block mb-16">
                        <loading size="small"></loading>
                    </div>
                    <div v-else class="grid grid-cols-3 gap-8">
                        <div
                            v-for="x in availableJobTypes"
                            :key="'x-' + x.value"
                            class="p-8 border border-primary rounded cursor-pointer"
                        >
                            <checkbox
                                v-model="form.type"
                                :value="x.value"
                            >
                                {{ x.label }}
                            </checkbox>
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <div v-if="fetchingAvailableParameters" class="inline-block mb-16">
                    <loading size="small"></loading>
                </div>
                <div class="grid grid-cols-3 gap-8">
                    <simple-input
                        v-model="form.shift_hours"
                        :max="37"
                        :min="1"
                        type="number"
                    >
                        {{ $t('jobs.hours_per_week') }}
                    </simple-input>
                </div>
            </div>

            <job-filter-groups :job="job" v-model="selectedFilters"/>

            <h2 class="text-body font-semibold first-letter:uppercase">
                {{ $t('jobs.create.headers.image') }}
            </h2>

            <div>
                <div class="w-full pt-40pct relative">
                    <div class="absolute inset-0 h-full w-full">
                        <siu v-model="form.photo_path_queued_id"
                             :image="form.image_url"
                             @clear="form.image_url = $event"
                             classes="h-full w-full shadow-md rounded"
                             icon-location="corner"
                             ratio="4:1,3"
                        ></siu>
                    </div>
                </div>
            </div>

            <h2 class="text-body font-semibold first-letter:uppercase">
                {{ $t('jobs.create.headers.video_url') }}
            </h2>

            <div>
                <simple-input
                    v-model="form.video_url"
                    class=""
                    placeholder="https://www.youtube.com/watch?v=_NXzezhPgFQ  |  https://vimeo.com/297911183"
                >
                    {{ $t('jobs.create.headers.video_url') }}
                </simple-input>
            </div>

            <h2 class="text-body font-semibold first-letter:uppercase">
                {{ $t('jobs.create.headers.describe') }}
            </h2>

            <translatable #default="{ languages, selectedLanguage }" :errors="warningTabsDescription">

                <div v-for="lang in languages" :key="lang">
                    <template v-if="lang === selectedLanguage">
                        <simple-input
                            v-model="form.description_title[lang]"
                            :placeholder="$tc('companypage.in_search_of', lang) + ' ...'"
                            class="mb-16">
                        </simple-input>

                        <wysiwyg
                            v-model="form.description[lang]"
                            class="mb-40"
                            full-featured
                        >
                        </wysiwyg>

                        <simple-input
                            v-model="form.profile_title[lang]"
                            :placeholder="$tc('companypage.what_is_expected', lang)"
                            class="mb-16">
                        </simple-input>

                        <wysiwyg
                            v-model="form.profile[lang]"
                            class="mb-40"
                            full-featured
                        >
                        </wysiwyg>

                        <simple-input
                            v-model="form.offer_title[lang]"
                            :placeholder="$tc('companypage.offer', lang)"
                            class="mb-16">
                        </simple-input>

                        <wysiwyg
                            v-model="form.offer[lang]"
                            class="mb-40"
                            full-featured
                        >
                        </wysiwyg>
                    </template>
                </div>

            </translatable>

            <h2 class="text-body font-semibold first-letter:uppercase">
                {{ $t('jobs.create.headers.attachments') }}
            </h2>

            <translatable #default="{ languages, selectedLanguage }">
                <div v-for="lang in languages" :key="lang" class="flex flex-wrap items-center">
                    <file-upload
                        v-show="lang === selectedLanguage"
                        v-model="form.queued_attachments[lang]"
                        :allData="true"
                        :multiple="true"
                        class="w-full mb-40"
                    >

                        <ul class="rounded-6 overflow-hidden w-full mb-16">
                            <li
                                v-for="attachment in attachments[lang]"
                                v-show="lang === selectedLanguage"
                                :key="'attachment-' + attachment.id"
                                class="odd:bg-primary-5 relative"
                            >
                                <a
                                    :download="!extensions.includes(attachment.extension) ? attachment.original_name : null"
                                    :href="attachment.url"
                                    :target="extensions.includes(attachment.extension) ? '_blank' : null"
                                    class="group flex items-center p-16 cursor-pointer">
                                    <div class="mr-auto font-semibold group-hover:text-primary trans-ease">{{ attachment.title[lang] }}</div>
                                    <div class="pl-16">
                                        <div class="h-32 w-32 flex items-center justify-center rounded-6 text-primary-80 trans-ease group-hover:text-primary mr-32">
                                            <svg-icon class="max-w-16 max-h-16" type="download"></svg-icon>
                                        </div>
                                    </div>
                                </a>

                                <div class="absolute right-0 inset-y-0 mr-16 flex items-center pointer-events-none">
                                    <button class="text-primary-80 hover:text-semantic-error cursor-pointer trans-ease pointer-events-auto h-32 w-32 flex items-center justify-center rounded-6"
                                            @click="confirmDeleteAttachment(attachment)">
                                        <svg-icon class="max-w-16 max-h-16" type="delete"></svg-icon>
                                    </button>
                                </div>
                            </li>
                        </ul>
                    </file-upload>
                </div>
            </translatable>

            <job-meta-data-editor
                v-model="form"
                :image="job ? job.og_image_url : null"
            />

            <div>
                <toggle
                    v-model="editPdfSettings"
                    @change="pdfSettingsUpdated"
                >
                    {{ $t('jobs.pdf_edit_settings') }}
                </toggle>

                <translatable v-if="editPdfSettings" #default="{ languages, selectedLanguage }" :errors="warningTabsPdf"
                              class="mt-16">

                    <div v-for="lang in languages" :key="lang">

                        <simple-input
                            v-show="lang === selectedLanguage"
                            v-model="form.title_pdf[lang]"
                            class="mb-16"
                        >
                            {{ $t('jobs.pdf_title') }}
                            <span v-if="languages.length > 1">({{ lang }})</span>
                        </simple-input>

                        <wysiwyg
                            v-show="lang === selectedLanguage" v-model="form.text_pdf[lang]"
                            class="mb-16"
                            full-featured
                        >
                            {{ $t('jobs.pdf_description') }}
                            <span v-if="languages.length > 1">({{ lang }})</span>
                        </wysiwyg>

                        <toggle
                            v-show="lang === selectedLanguage"
                            v-model="form.use_auto_pdf[lang]"
                            class="mb-16"
                        >
                            {{ $t('jobs.pdf_use_auto') }}
                            <span v-if="languages.length > 1">({{ lang }})</span>
                        </toggle>

                        <toggle
                            v-show="lang === selectedLanguage"
                            v-model="form.logo_in_pdf[lang]"
                            class="mb-16"
                        >
                            {{ $t('jobs.pdf_show_logo') }}
                            <span v-if="languages.length > 1">({{ lang }})</span>
                        </toggle>

                        <toggle
                            v-show="lang === selectedLanguage"
                            v-model="form.button_in_pdf[lang]"
                        >
                            {{ $t('jobs.pdf_show_button') }}
                            <span v-if="languages.length > 1">({{ lang }})</span>
                        </toggle>
                    </div>

                </translatable>
            </div>

            <div>
                <toggle v-model="editExtraTranslations">
                    {{ $t('jobs.toggle_extra_translations') }}
                </toggle>

                <translatable v-if="editExtraTranslations" #default="{ languages, selectedLanguage }" class="mt-16">
                    <div v-for="lang in languages" :key="lang">
                        <div class="grid grid-cols-2 gap-x-32 gap-y-16" v-show="lang === selectedLanguage">
                            <simple-input
                                :placeholder="$t('companypage.apply')"
                                v-model="form.extra[lang].application_button"
                                :input-label="$t('jobs.apply_to_job_button') + (languages.length > 1 ? ' (' + lang + ')' : '')"
                            />
                            <simple-input
                                :placeholder="$t('companypage.apply_confirmation')"
                                v-model="form.extra[lang].send_application_button"
                                :input-label="$t('jobs.send_application_button') + (languages.length > 1 ? ' (' + lang + ')' : '')"
                            />
                            <simple-input
                                :placeholder="$t('jobs.view_opening')"
                                v-model="form.extra[lang].go_to_application_button"
                                :input-label="$t('jobs.go_to_job_button') + (languages.length > 1 ? ' (' + lang + ')' : '')"
                            />
                        </div>
                    </div>

                </translatable>
            </div>

            <confirmation-templates
                v-model="form.confirmation_template_id"
                class="max-w-1/2"
            ></confirmation-templates>

            <div>
                <div class="flex items-center justify-end">
                    <simple-button
                        id="save-and-close"
                        :loading="form.submitting"
                        class="mr-8"
                        display="primary"
                        icon="save"
                        @click="save('close')"
                    >
                      {{ $t('jobs.save_and_close') }}
                    </simple-button>
                    <simple-button
                        id="save-and-next"
                        :loading="form.submitting"
                        look="primary"
                        display="secondary"
                        icon="save"
                        @click="save('next')"
                    >
                      {{ $t('jobs.save_and_next') }}
                    </simple-button>
                </div>
            </div>

            <popup-modal-confirm
                v-if="deletingAttachment"
                :confirmText="$t('general.delete_subject', {subject: $t('jobs.attachment')})"
                :message="$t('general.confirm_delete_subject', {subject: deletingAttachment.title[locale]})"
                :working="performingDeleteAttachmentRequest"
                @close="cancelDeleteAttachment"
                @confirm="deleteAttachment(deletingAttachment)"
            >
            </popup-modal-confirm>
        </div>

        <popup-modal-translate
            v-if="translatePopup"
            :message="'Vertalen'"
            :form="form"
            :translatableType="translatableType"
            @close="toggleTranslatePopup"
        />

    </div>
</template>

<script>
import Checkbox from '../forms/Checkbox.vue';
import FileUpload from '../forms/FileUpload.vue';
import Attachment from '../../models/Attachment';
import Translatable from '../forms/Translatable.vue';
import Input from '../forms/Input.vue';
import Loading from '../../components/ui/Loading.vue';
import SingleImageUpload from '../forms/SingleImageUpload.vue';
import Wysiwyg from '../forms/Wysiwyg.vue';
import SelectDropdown from '../forms/SelectDropdown.vue';
import Form from '../../etc/forms/Form';
import SvgIcon from './SvgIcon.vue';
import _kebabCase from 'lodash/kebabCase';
import SimpleButton from './SimpleButton.vue';
import Toggle from '../forms/Toggle.vue';
import {normalizeJson, openableFileExtensionsInWebBrowser, buildDropdownOptions} from '../../../shared/helpers';
import * as jobs from '../../services/JobService';
import * as attachmentService from '../../services/AttachmentService';
import TextArea from '../forms/TextArea.vue';
import ConfirmationTemplateSelector from './ConfirmationTemplateSelector.vue';
import JobLocationEditor from '../JobLocationEditor';
import JobMetaDataEditor from '../JobMetaDataEditor';
import OverlayLocked from './OverlayLocked';
import PopupModalTranslate from "./PopupModalTranslate";
import JobFilters from './JobFilters';
import PopupModalConfirm from './PopupModalConfirm';
import {isArray} from "lodash";
import JobReason from "../../models/JobReason";
import * as JobReasonService from '../../services/JobReasonService';
import JobFilterGroups from "./JobFilterGroups.vue";
import Filter from "../../models/Filter";
import {mapStores} from "pinia";
import {useJobsStore} from "../../state/modules/jobs";
import {useCompaniesStore} from "../../state/modules/companies";
import {useMainStore} from "../../state/store";
import {useRepo} from "pinia-orm";

function getFields(languages) {
    let attachments = {};
    let use_auto_pdf = {};
    let extra_translations = {};

    for (let lang of languages) {
        attachments[lang] = [];
        use_auto_pdf[lang] = true;
        extra_translations[lang] = {
            'application_button': null,
            'go_to_application_button': null,
            'send_application_button': null,
        };
    }

    return [
        {name: 'title', value: {}, rules: ['required'], translatable: true},
        {name: 'slug', value: {}, rules: ['required'], translatable: true},
        {name: 'description', value: {}, rules: ['required'], translatable: true},
        {name: 'description_title', value: {}, rules: ['required'], translatable: true},
        {name: 'profile', value: {}, rules: ['required'], translatable: true},
        {name: 'profile_title', value: {}, rules: ['required'], translatable: true},
        {name: 'offer', value: {}, rules: ['required'], translatable: true},
        {name: 'offer_title', value: {}, rules: ['required'], translatable: true},
        {name: 'og_title', value: {}, rules: ['required'], translatable: true},
        {name: 'og_description', value: {}, rules: ['required'], translatable: true},
        {name: 'use_auto_pdf', value: {...use_auto_pdf}, rules: ['required'], translatable: true},
        {name: 'title_pdf', value: {}, rules: ['required'], translatable: true},
        {name: 'text_pdf', value: {}, rules: ['required'], translatable: true},
        {name: 'logo_in_pdf', value: true, rules: ['required'], translatable: true},
        {name: 'button_in_pdf', value: false, rules: ['required'], translatable: true},
        {name: 'reference_number', value: '', rules: ['required']},
        {name: 'extra', value: {...extra_translations}, translatable: true},
        {name: 'company_location_id', value: null},
        {name: 'postal_code', value: null},
        {name: 'city', value: null},
        {name: 'country', value: null, rules: ['required']},
        {name: 'remote', value: 0},
        {name: 'image_url', value: null},
        {name: 'photo_path_queued_id', value: null},
        {name: 'og_image_queued_id', value: null},
        {name: 'video_url', value: null},
        {name: 'type', value: []},
        {name: 'timing', value: []},
        {name: 'shift_hours', value: null},
        {name: 'status', value: null},
        {name: 'contract_type', value: null},
        {name: 'wanted_experience', value: null},
        {name: 'queued_attachments', value: {...attachments}},
        {name: 'confirmation_template_id', value: null},
        {name: 'request_reason_ids', value: []},
    ];
}

export default {
    components: {
        JobFilterGroups,
        PopupModalConfirm,
        JobFilters,
        JobMetaDataEditor,
        JobLocationEditor,
        PopupModalTranslate,
        'checkbox': Checkbox,
        'loading': Loading,
        'translatable': Translatable,
        'simple-input': Input,
        'siu': SingleImageUpload,
        'wysiwyg': Wysiwyg,
        'select-dropdown': SelectDropdown,
        'svg-icon': SvgIcon,
        'simple-button': SimpleButton,
        'toggle': Toggle,
        'file-upload': FileUpload,
        'simple-textarea': TextArea,
        'confirmation-templates': ConfirmationTemplateSelector,
        'overlay-locked': OverlayLocked,
    },
    props: {
        job: Object,
    },
    setup() {
        const mainStore = useMainStore();
        return {mainStore};
    },
    data: function () {
        return {
            editPdfSettings: false,
            editExtraTranslations: false,
            useCustomAutoReply: false,
            selectedFilters: {},
            form: null,
            slugShouldUpdate: this.getSlugUpdateSettings(),
            deletingAttachment: null,
            performingDeleteAttachmentRequest: false,
            attachments: {},
            extensions: openableFileExtensionsInWebBrowser,
            seekFeatureEnabled: false,

            translatePopup: false,
            translatableType: null,

            fetchingLimits: false,
            fetchingReasons: false,
            limits: {
                slots: null,
                count: null,
            },
        };
    },
    computed: {
        ...mapStores(useMainStore, useJobsStore, useCompaniesStore),
        locale() {
            return this.mainStore.locale;
        },
        availableParameters() {
            return this.jobsModuleStore.availableParameters;
        },
        fetchingAvailableParameters() {
            return this.jobsModuleStore.fetchingAvailableParameters;
        },
        currentCompany() {
            return this.mainStore.currentCompany;
        },
        requestReasons() {
            return useRepo(JobReason)
                .where('is_rejected', 0)
                .get();
        },
        availableTimings() {
            return this.availableSomethingMultiple('timing', this.availableParameters);
        },
        availableJobTypes() {
            return this.availableSomethingMultiple('type', this.availableParameters);
        },
        availableStatuses() {
            return this.availableSomething('status', this.availableParameters);
        },
        availableContractTypes() {
            return this.availableSomething('contract_type', this.availableParameters);
        },
        availableWantedExperiences() {
            return this.availableSomething('wanted_experience', this.availableParameters);
        },
        getFilters() {
            return useRepo(Filter).all();
        },
        relationships() {
            return {
                filters: {
                    data: this.selectedFilters,
                },
            };
        },
        limitReached() {
            if (this.job) {
                return false;
            }

            if (!this.limits.slots) {
                return false;
            }

            return this.limits.count >= this.limits.slots;
        },
        getAddressOfLocation() {
            if (!this.form.company_location_id) {
                return {};
            }

            if (!this.availableParameters.locations) {
                return {};
            }

            const location = this.availableParameters.locations.find(i => i.value === parseInt(this.form.company_location_id));

            if (!location) {
                return {};
            }

            return location;
        },
        warningTabsTitle() {
            let languages = this.currentCompany.jobsite.active_languages;
            let errorLang = [];

            for (let index in languages) {
                if (!this.form.data.title[languages[index]]) {
                    errorLang.push(languages[index]);
                }
            }
            return errorLang;
        },
        warningTabsDescription() {
            let languages = this.currentCompany.jobsite.active_languages;
            let errorLang = [];

            for (let index in languages) {
                if (!this.form.data.description_title[languages[index]]) {
                    errorLang.push(languages[index]);
                }
            }
            return errorLang;
        },
        warningTabsPdf() {
            let languages = this.currentCompany.jobsite.active_languages;
            let errorLang = [];

            for (let index in languages) {
                if (!this.form.data.title_pdf[languages[index]]) {
                    errorLang.push(languages[index]);
                }
            }
            return errorLang;
        },
        referenceNumberLabelPrefix() {
            return this.seekFeatureEnabled ? 'seek' : '';
        },
    },
    methods: {
        availableSomething(something, source, defaultText = null) {

            if (!defaultText) {
                defaultText = this.$t('general.select_subject', {subject: this.$t('jobs.labels.' + something)});
            }

            let defaultOption = {
                value: null,
                label: '--- ' + defaultText + ' ---',
            };

            if (typeof source[something] === 'undefined') {
                return [defaultOption];
            }

            let options = source[something]
                .filter(value => {
                    if (value.countries) {
                        // If countries is part of the item then we'll only show the option if it's part of the selected countries
                        const countryCode = !!this.form.company_location_id ? this.getAddressOfLocation.country_code : this.form.country;
                        return value.countries.map(i => i.toLowerCase()).includes(countryCode?.toLowerCase());
                    }

                    return true;
                })
                .map(value => ({
                    value: value.value, label: value.translations[this.locale] || '',
                }))
                .sort((a, b) => a.label.localeCompare(b.label));

            if (options.length === 0) {
                defaultOption.label = this.$t('job_parameters.unavailable');
            }

            return [defaultOption, ...options];
        },
        availableSomethingMultiple(something, source) {
            if (typeof source[something] === 'undefined') {
                return [];
            }

            let options = source[something].map(value => ({
                value: value.name, label: value.translations[this.locale] || '',
            }));

            return options;
        },
        generateForm() {
            let fields = getFields(this.currentCompany.jobsite.active_languages);

            if (this.job) {
                fields.forEach((field, index) => {
                    if (typeof this.job[field.name] !== 'undefined') {
                        let value = JSON.parse(JSON.stringify(this.job[field.name]));
                        if (field.name === "extra") {
                            fields[index].value = this.mergeExtraTranslations(fields[index].value, value);
                        }

                        fields[index].value = value;
                    }
                });
            } else {
                fields.forEach((field, index) => {
                    if (field.name === 'type') {
                        //Set a default jobtype
                        fields[index].value = ['fulltime'];
                    }
                });
            }

            return new Form(fields);
        },
        mergeExtraTranslations(initValues, dbValues) {
            Object.keys(initValues).forEach(lang => {
                if (!dbValues[lang] || (isArray(dbValues[lang]) && !dbValues[lang].length)) {
                    dbValues[lang] = initValues[lang];
                }
            });

            return dbValues;
        },
        checkIfExtraTranslations() {
            this.editExtraTranslations = false;
            if (!this.form.extra) {
                return;
            }

            Object.keys(this.form.extra).forEach((lang) => {
                if (!this.editExtraTranslations && !isArray(this.form.extra[lang]) && this.form.extra[lang] && (Object.values(this.form.extra[lang]).some(v => v))) {
                    this.editExtraTranslations = true;
                }
            });
        },
        initAttachments() {
            let result = {};

            for (let lang of
                this.mainStore.availableLanguages.map(lang => lang.value)) {
                result[lang] = [];
            }
            return result;
        },
        generateAttachments() {
            let attachments = this.initAttachments();

            if (this.job && this.job.attachments.length > 0) {
                for (let attachment of this.job.attachments) {
                    if (attachments[attachment.locale]) {
                        attachments[attachment.locale].push(attachment);
                    }
                }
            }

            return attachments;
        },
        getSlugUpdateSettings() {
            let settings = {};
            let languages = this.mainStore.currentCompany.jobsite.active_languages;

            for (let index in languages) {
                if (!this.job || !this.job.title[languages[index]]) {
                    settings[languages[index]] = true;
                } else {
                    settings[languages[index]] = this.job.title[languages[index]].toLowerCase().includes(this.$t('general.copy', languages[index]).toLowerCase());
                }
            }

            return settings;
        },
        updateSlug(value, lang) {
            if (this.slugShouldUpdate[lang]) {
                this.form.slug[lang] = _kebabCase(value);
            }
        },
        toggleSlugUpdating(lang) {
            this.slugShouldUpdate[lang] = !this.slugShouldUpdate[lang];

            if (this.slugShouldUpdate[lang]) {
                this.form.slug[lang] = _kebabCase(this.form.title[lang]);
            }
        },
        toggleTranslationPopup(label = '', translatableType = '', targetLanguage = '') {
            this.translatableType = {
                label,
                type: translatableType,
                lang: targetLanguage,
                translations: this.form.data
            };
            this.translatePopup = !this.translatePopup;
        },
        updateSlugFromInput(value, lang) {
            this.form.slug[lang] = _kebabCase(value);
            this.slugShouldUpdate[lang] = false;
        },
        wantedExperienceChanged(value) {
            // this field is now optional
            // make sure that we pass NULL instead of undefined
            if (typeof value == 'undefined') {
                this.form.wanted_experience = null;
            }
        },
        confirmDeleteAttachment(attachment) {
            this.deletingAttachment = attachment;
        },
        cancelDeleteAttachment() {
            this.deletingAttachment = null;
        },
        deleteAttachment(attachment) {
            this.performingDeleteAttachmentRequest = true;

            attachmentService.destroy(attachment.id)
                .then(() => useRepo(Attachment).destroy(attachment.id))
                .then(() => {
                    this.attachments[attachment.locale] = this.attachments[attachment.locale].filter(att => att.id !== attachment.id);

                    this.deletingAttachment = null;
                    this.performingDeleteAttachmentRequest = false;
                });
        },
        pdfSettingsUpdated($val) {
            if ($val === false) {
                for (let lang in this.form.text_pdf) {
                    if (this.form.text_pdf.hasOwnProperty(lang)) {
                        this.form.text_pdf[lang] = null;
                        this.form.title_pdf[lang] = null;
                    }
                }
            }
        },
        fixDropdownValues() {
            if (!this.availableContractTypes.find(i => i.value === parseInt(this.form.contract_type))) {
                this.form.contract_type = null;
            }

            if (!this.availableStatuses.find(i => i.value === parseInt(this.form.status))) {
                this.form.status = null;
            }
        },
        save(type) {
            this.$emit('save', {
                todo: type,
                form: this.form,
                relationships: this.relationships
            });
            this.checkIfExtraTranslations();
        },
        toggleTranslatePopup() {
            this.translatePopup = !this.translatePopup;
        },
        fetchJobReasons() {
            this.fetchingReasons = true
            JobReasonService.index()
                .then(({data}) => {
                    return useRepo(JobReason).save(normalizeJson(data))
                })
                .then(() => {
                    this.fetchingReasons = false
                })
        },
		setAccessToFeature() {
            this.seekFeatureEnabled = !!this.companiesModuleStore.hasFeature('seek')
        },
    },
    created() {
        this.attachments = this.generateAttachments();
        this.form = this.generateForm();
        this.checkIfExtraTranslations();
    },
    watch: {
        availableParameters: {
            deep: true,
            handler() {
                this.fixDropdownValues();
            },
        },
        form: {
            deep: true,
            handler() {

                // Don't fix dropdown values before the parameters have
                // been fetched from the backend!
                if (this.fetchingAvailableParameters) {
                    return;
                }

                this.fixDropdownValues();
            },
        },
    },
    mounted() {
        if (!useRepo(JobReason).first()) {
            this.fetchJobReasons()
        }

        if ((this.form.text_pdf[this.locale] || null) || (this.form.title_pdf[this.locale] || null)) {
            this.editPdfSettings = true;
        }

        if (!this.job) {
            this.fetchingLimits = true;
            jobs.limits().then(data => {
                this.limits = data;
            }).finally(() => this.fetchingLimits = false);
        }

        this.setAccessToFeature();
    },
};
</script>
